import { useEffect, useState } from 'react';
import Wrapper from '@/ui//wrapper';
import { getAnalyticsTabList } from '@/api/analytics';
import { saveAnalyticsTabList } from '@/services/analytics/Action';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useAmplitude from '@/hooks/useAmplitude';
import NoContent from '@/ui//NoContent';
import SubscriptionCTAModal from '@/ui//modals/subscriptionCTAModal/SubscriptionCTAModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlusIcon } from 'lucide-react';
import DataAnalysis from '@/components/analyticsPage/tabPanels/components/dataAnalysis';
import IndexAnalysis from '@/components/analyticsPage/tabPanels/components/indexAnalysis';
import { Button } from '@/components/ui/button';
import InitiateCTA from '@/components/analyticsPage/tabPanels/components/initiateCTA';
import GeneratorModal from '@/components/analyticsPage/tabPanels/components/initiateCTA/components/generatorModal';
import IndexDrawer from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer';
import DataDrawer from '@/components/analyticsPage/tabPanels/components/drawers/dataDrawer';

const Analytics = () => {
  useAmplitude('analytics');

  const dispatch = useDispatch();
  const { id } = useParams();
  const user = useSelector((state: any) => state.user.info);
  const [selectTab, setSelectTab] = useState<string | null>(null);
  const { tabList } = useSelector((state: any) => state.analytics);
  const [isOpen, setIsOpen] = useState(false);
  const isValidUser = user?.user_type === 'pro' || user?.user_type === 'enterprise' ? true : false;

  const [openGeneratorModal, setOpenGeneratorModal] = useState<boolean>(false);
  const [openDataDrawer, setOpenDataDrawer] = useState<boolean>(false);
  const [openIndexDrawer, setOpenIndexDrawer] = useState<boolean>(false);

  const navigate = useNavigate();

  const fetchTabList = async () => {
    const results = await getAnalyticsTabList(user.id);
    dispatch(saveAnalyticsTabList(results.data));
    return results.data;
  };

  useEffect(() => {
    if (!isValidUser) return setIsOpen(true);
    init(id);
  }, [id]);

  const init = async (id?: string) => {
    if (id) {
      setSelectTab(id);
    } else {
      const l = await fetchTabList();
      if (l && l.length > 0) {
        setSelectTab(String(l[0].id));
      }
    }
  };

  return (
    <>
      <Wrapper className="content-wrapper pt-12 max-w-[90%] mx-auto">
        {isValidUser ? (
          <Tabs value={selectTab} onValueChange={(value) => navigate(`/analytics/${value}`)}>
            <div className="flex items-center gap-3">
              {tabList && tabList.length > 0 && (
                <TabsList>
                  {tabList?.map((tab: any) => (
                    <TabsTrigger
                      key={tab.id}
                      value={String(tab.id)}
                      className={selectTab === String(tab.id) ? 'bg-white' : ''}
                    >
                      {tab.tab_name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              )}
              <Button
                variant="outline"
                className="rounded-full"
                size="icon"
                onClick={() => setOpenGeneratorModal(true)}
              >
                <PlusIcon className="w-4 h-4" />
              </Button>
            </div>
            {tabList?.map((tab: any) => (
              <TabsContent value={String(tab.id)} key={tab.id + 'content'}>
                {tab.tab_type.includes('data') ? (
                  <DataAnalysis tab={tab} />
                ) : (
                  <IndexAnalysis tab={tab} />
                )}
              </TabsContent>
            ))}
            {tabList && tabList.length === 0 && <InitiateCTA />}
          </Tabs>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
            }}
          >
            <NoContent />
          </div>
        )}
      </Wrapper>
      <GeneratorModal
        open={openGeneratorModal}
        closeModal={() => setOpenGeneratorModal(false)}
        handleDataDrawer={() => setOpenDataDrawer(true)}
        handleIndexDrawer={() => setOpenIndexDrawer(true)}
      />
      <IndexDrawer
        open={openIndexDrawer}
        closeDrawer={() => setOpenIndexDrawer(false)}
        action="create"
      />
      <DataDrawer
        open={openDataDrawer}
        closeDrawer={() => setOpenDataDrawer(false)}
        action="create"
      />
      <SubscriptionCTAModal open={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default Analytics;
